<template>
  <svg
    class="app-icon"
    :width="px"
    :height="px"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    :stroke-width="strokeWidth"
    stroke-linecap="round"
    stroke-linejoin="round"
    :aria-hidden="label ? undefined : 'true'"
    :aria-label="label || undefined"
    :role="label ? 'img' : undefined"
    focusable="false"
  >
    <path
      v-for="(part, index) in parts"
      :key="index"
      :d="part.d"
      :fill="part.fill ? 'currentColor' : 'none'"
      :stroke="part.stroke === false ? 'none' : 'currentColor'"
      :stroke-width="part.sw || undefined"
    />
  </svg>
</template>

<script setup>
/**
 * AppIcon — набор inline-SVG иконок (24×24, цвет = currentColor).
 *
 * <AppIcon name="star" :size="20" />          // filled
 * <AppIcon name="star-outline" />
 * <AppIcon name="info" label="Правила" />     // label → role="img" + aria-label
 *
 * Иконки: star, star-outline, crown, trophy, play, info, pencil, chevron-left, chevron-right,
 * copy, document, book-open, eye, mouth, close, bell, share, refresh, check, warning, camera, clock, user, users, link, send.
 */
import { computed } from "vue";

const props = defineProps({
  /** Имя иконки из набора ниже. */
  name: { type: String, required: true },
  /** Размер в px (число) или CSS-строка ('1em'). */
  size: { type: [Number, String], default: 24 },
  /** Толщина линий для контурных иконок. */
  strokeWidth: { type: [Number, String], default: 2 },
  /** Доступное имя; без него иконка декоративная (aria-hidden). */
  label: { type: String, default: "" },
});

const STAR = "M12 2.8l2.76 5.6 6.18.9-4.47 4.36 1.05 6.15L12 16.9l-5.52 2.91 1.05-6.15L3.06 9.3l6.18-.9L12 2.8z";

const ICONS = {
  star: [{ d: STAR, fill: true, sw: 1.6 }],
  "star-outline": [{ d: STAR }],
  crown: [
    { d: "M3.5 8.5l4.2 3.6L12 5.5l4.3 6.6 4.2-3.6-1.7 9.2a1.2 1.2 0 0 1-1.18.98H6.38a1.2 1.2 0 0 1-1.18-.98L3.5 8.5z", fill: true, sw: 1.4 },
    { d: "M3.5 6.6h.01M12 3.6h.01M20.5 6.6h.01", sw: 3 },
  ],
  trophy: [
    { d: "M7 3.5h10v5.2a5 5 0 0 1-10 0V3.5z", fill: true, sw: 1.4 },
    { d: "M7 5.5H4.5v1.2A3.3 3.3 0 0 0 7.6 10M17 5.5h2.5v1.2a3.3 3.3 0 0 1-3.1 3.3" },
    { d: "M12 13.7v3.3" },
    { d: "M8.5 20.5h7l-.8-3.5H9.3l-.8 3.5z", fill: true, sw: 1.4 },
  ],
  play: [{ d: "M7.5 5.2v13.6a1 1 0 0 0 1.53.85l10.6-6.8a1 1 0 0 0 0-1.7L9.03 4.35A1 1 0 0 0 7.5 5.2z", sw: 2.4 }],
  info: [
    { d: "M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z" },
    { d: "M12 11v5.5" },
    { d: "M12 7.6h.01", sw: 2.6 },
  ],
  pencil: [
    { d: "M15.3 4.7a2.1 2.1 0 0 1 3 3L8.5 17.5 4.5 19.5l2-4 8.8-10.8z", fill: true, sw: 1.4 },
    { d: "M13.8 6.4l3 3", sw: 1.4 },
  ],
  "chevron-left": [{ d: "M14.5 5.5L8 12l6.5 6.5", sw: 2.4 }],
  "chevron-right": [{ d: "M9.5 5.5L16 12l-6.5 6.5", sw: 2.4 }],
  copy: [
    { d: "M9.5 8.5h9a1.5 1.5 0 0 1 1.5 1.5v9a1.5 1.5 0 0 1-1.5 1.5h-9A1.5 1.5 0 0 1 8 19v-9a1.5 1.5 0 0 1 1.5-1.5z" },
    { d: "M5 15.5h-.5A1.5 1.5 0 0 1 3 14V5a1.5 1.5 0 0 1 1.5-1.5h9A1.5 1.5 0 0 1 15 5v.5" },
  ],
  "book-open": [
    { d: "M12 5.5C9 3.5 5.5 3 2.5 4.5v15C5.5 18 9 18.5 12 20.5c3-2 6.5-2.5 9.5-1v-15C18.5 3 15 3.5 12 5.5z" },
    { d: "M12 5.5v15" },
  ],
  document: [
    { d: "M14 3H7.5A2.5 2.5 0 0 0 5 5.5v13A2.5 2.5 0 0 0 7.5 21h9a2.5 2.5 0 0 0 2.5-2.5V8l-5-5z" },
    { d: "M14 3v5h5" },
    { d: "M9 12.5h6M9 16.5h4M9 8.5h2" },
  ],
  eye: [
    { d: "M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12z" },
    { d: "M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z", fill: true, sw: 1 },
  ],
  mouth: [
    { d: "M3.5 9.2C6 8 9 7.5 12 7.5s6 .5 8.5 1.7c-.6 5.2-4.1 8.8-8.5 8.8S4.1 14.4 3.5 9.2z" },
    { d: "M7 10.6c1.6.5 3.3.7 5 .7s3.4-.2 5-.7" },
    { d: "M9 15.3c1-.5 2-.7 3-.7s2 .2 3 .7" },
  ],
  close: [{ d: "M6 6l12 12M18 6L6 18", sw: 2.4 }],
  bell: [
    { d: "M6 16.5V11a6 6 0 1 1 12 0v5.5l1.5 2h-15l1.5-2z" },
    { d: "M10 20.5a2 2 0 0 0 4 0" },
  ],
  share: [
    { d: "M4.5 12.5v6a1.5 1.5 0 0 0 1.5 1.5h12a1.5 1.5 0 0 0 1.5-1.5v-6" },
    { d: "M12 3.5v11.5" },
    { d: "M7.5 8L12 3.5 16.5 8" },
  ],
  refresh: [
    { d: "M20 11.5a8 8 0 1 0-2.35 5.65" },
    { d: "M20.5 4.5v7h-7" },
  ],
  check: [{ d: "M5 12.5l4.5 4.5L19 7.5", sw: 2.6 }],
  warning: [
    { d: "M10.3 4.2L2.8 17.5a2 2 0 0 0 1.7 3h15a2 2 0 0 0 1.7-3L13.7 4.2a2 2 0 0 0-3.4 0z" },
    { d: "M12 9.5v4.5" },
    { d: "M12 17.4h.01", sw: 2.6 },
  ],
  camera: [
    { d: "M3.5 8.5A2 2 0 0 1 5.5 6.5h2l1.5-2h6l1.5 2h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-13a2 2 0 0 1-2-2v-9z" },
    { d: "M12 16.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z" },
  ],
  clock: [
    { d: "M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z" },
    { d: "M12 7.5V12l3 2" },
  ],
  link: [
    { d: "M10 13.5a4 4 0 0 0 6 .5l3-3a4 4 0 0 0-5.7-5.7l-1.6 1.6M14 10.5a4 4 0 0 0-6-.5l-3 3a4 4 0 0 0 5.7 5.7l1.6-1.6" },
  ],
  send: [
    { d: "M21 3 3 10.5l7.5 3L13.5 21 21 3zM10.5 13.5 21 3" },
  ],
  users: [
    { d: "M9 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z" },
    { d: "M2.5 20v-1a6.5 6.5 0 0 1 13 0v1M16 4.5a3.5 3.5 0 0 1 0 6.5M17.5 13.5a6 6 0 0 1 4 5.5v1" },
  ],
  user: [
    { d: "M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8z" },
    { d: "M4.5 20.5c.8-3.6 3.8-6 7.5-6s6.7 2.4 7.5 6" },
  ],
};

const parts = computed(() => {
  const icon = ICONS[props.name];
  if (!icon && process.env.NODE_ENV === "development") console.warn(`[AppIcon] неизвестная иконка "${props.name}"`);
  return icon || [];
});

const px = computed(() => (typeof props.size === "number" ? `${props.size}px` : props.size));
</script>

<style scoped>
.app-icon {
  display: inline-block;
  flex-shrink: 0;
  vertical-align: middle;
}
</style>
